<template>
  <div class="wrapper no-scroll-bar">
    <div class="header-wrapper">
      <v-toolbar color="primary">
        <v-btn @click="routerBack" style="z-index: 1;" icon>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title
          style="
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            margin: 0;
            padding: 0;
            max-width: 60%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
          class="text-center"
        >
          {{ childName }}
        </v-toolbar-title>

        <div style="width: 70px; margin-left: auto;">
          <v-btn @click="toChat" icon>
            チャット
          </v-btn>
        </div>
      </v-toolbar>

      <v-card tile elevation="0" style="height:60px;">
        <div class="pa-1 date_card">
          <h5 class="text-center">{{ year }}年</h5>
        </div>
        <div class="date_card d-flex align-center justify-space-between">
          <div class="d-flex align-center ml-3">
            <v-btn
              @click="toPrevDate"
              class="no-background-hover"
              color="primary"
              variant="outlined--text"
              dark>
              <v-icon>mdi-chevron-left</v-icon>
              <h4 class="primary--text">前の日</h4>
            </v-btn>
          </div>

          <div class="d-flex align-end">
            <h3>{{ today }}</h3>
            <p>{{ `（${dayOfTheWeek}）` }}</p>
          </div>

          <div class="d-flex align-center mr-3">
            <v-btn
              @click="toNextDate"
              class="no-background-hover"
              color="primary"
              variant="outlined--text"
              dark>
              <h4 class="primary--text">次の日</h4>
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
      <div class="grey-border" />
    </div>

    <div class="content-wrapper no-scroll-bar">
      <div class="title-wrapper pa-1 px-3">
        <h5>園での記録</h5>
      </div>

      <div v-if="nurseryLoading" class="d-flex justify-center align-center pa-10">
        <vue-loading
          :active="nurseryLoading"
          loader="bars"
          color="#EE6090"
          :size="{ width: '30px', height: '30px' }"
        ></vue-loading>
      </div>

      <div v-else>
        <div class="subtitle-wrapper pa-1 px-3">
          <h5>登降園時間</h5>
        </div>
        <div
          class="d-flex pa-2"
          v-if="attendance && (attendance.attendedAt || attendance.leftAt)">
          <p>
            {{ minuteFormat(attendance.attendedAt) }} 〜
            {{ minuteFormat(attendance.leftAt) }}
            <span class="caption grey--text">
              （編集 {{ minuteFormat(attendance.updatedAt) }}）
            </span>
          </p>
        </div>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>睡眠時間</h5>
        </div>
        <div class="d-flex pa-2" v-for="s in nursery?.sleeps" :key="s.sleepId">
          <p>{{ minuteFormat(s.from) }} 〜 {{ minuteFormat(s.to) }}</p>
        </div>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>喫食</h5>
        </div>
        <div>
          <div class="d-flex flex-wrap">
            <template v-if="Array.isArray(meals) && meals.length">
              <div v-for="m in meals" :key="m.key" class="pa-1">
                <eating-status-item 
                  v-if="m && m.item"
                  :text="m.text"
                  :item="m.item"
                />
              </div>
            </template>
          </div>
          <div v-if="mealComment" class="pa-2">
            <p class="caption grey--text mb-1">コメント</p>
            <p>{{ mealComment || '' }}</p>
          </div>
        </div>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>排便</h5>
        </div>
        <div
          class="d-flex pa-2"
          v-for="d in nursery?.defecations"
          :key="d.defecationId">
          <p class="grey--text text--darken-1 mr-2">
            {{ minuteFormat(d.recordedAt) }}
          </p>
          <p>{{ getDefecationState(d.state) }}</p>
        </div>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>ミルク</h5>
        </div>
        <div class="d-flex pa-2" v-for="m in nursery?.milks" :key="m.milkId">
          <p class="grey--text text--darken-1 mr-2">
            {{ minuteFormat(m.recordedAt) }}
          </p>
          <p>
            {{
              `${categoryToText(m.milkCategory)}  ${
                m.amount ? m.amount.value + 'cc' : '不明'
              }`
            }}
          </p>
        </div>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>体温</h5>
        </div>
        <div
          class="d-flex pa-2"
          v-for="t in nursery?.temperatures"
          :key="t.temperatureId">
          <p class="grey--text text--darken-1 mr-2">
            {{ minuteFormat(t.recordedAt) }}
          </p>
          <p>{{ t.temperature }}℃</p>
        </div>
      </div>

      <div class="title-wrapper pa-1 px-3">
        <h5>家庭での記録</h5>
      </div>

      <div v-if="homeLoading" class="d-flex justify-center align-center pa-10">
        <vue-loading
          :active="homeLoading"
          loader="bars"
          color="#EE6090"
          :size="{ width: '30px', height: '30px' }"
        ></vue-loading>
      </div>

      <div v-else>
        <div class="subtitle-wrapper pa-1 px-3">
          <h5>睡眠時間</h5>
        </div>
        <sleep-list :sleeps="home.sleeps" />
        <div class="px-5 py-2">
          <v-btn
            @click="isSleepDialog = true"
            color="primary"
            variant="outlined"
            block
            small>
            睡眠記録を追加
          </v-btn>
        </div>
        <v-dialog v-model="isSleepDialog" persistent>
          <sleep-modal
            :isOpen="isSleepDialog"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport"
            @close="isSleepDialog = false" />
        </v-dialog>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>体温</h5>
        </div>
        <temperature-list :temperatures="home.temperatures" />
        <div class="px-5 py-2">
          <v-btn
            @click="isTemperatureDialog = true"
            color="primary"
            variant="outlined"
            block
            small>
            体温記録を追加
          </v-btn>
        </div>
        <v-dialog v-model="isTemperatureDialog" persistent>
          <temperature-modal
            :isOpen="isTemperatureDialog"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport"
            @close="isTemperatureDialog = false" />
        </v-dialog>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>朝食</h5>
        </div>
        <breakfast-list
          :breakfasts="home.breakfasts"
          :childId="childId"
          :selectedDate="selectedDate"
          @edited="fetchReport" />
        <div v-show="home.breakfasts.length === 0" class="px-5 py-2">
          <v-btn
            @click="isBreakfastDialog = true"
            color="primary"
            variant="outlined"
            block
            small>
            朝食記録を追加
          </v-btn>
        </div>
        <v-dialog v-model="isBreakfastDialog" persistent>
          <breakfast-modal
            :isOpen="isBreakfastDialog"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport"
            @close="isBreakfastDialog = false" />
        </v-dialog>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>夕食</h5>
        </div>
        <dinner-list
          :dinners="home.dinners"
          :childId="childId"
          :selectedDate="selectedDate"
          @edited="fetchReport" />
        <div v-show="home.dinners.length === 0" class="px-5 py-2">
          <v-btn
            @click="isDinnerDialog = true"
            color="primary"
            variant="outlined"
            block
            small>
            夕食記録を追加
          </v-btn>
        </div>
        <v-dialog v-model="isDinnerDialog" persistent>
          <dinner-modal
            :isOpen="isDinnerDialog"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport"
            @close="isDinnerDialog = false" />
        </v-dialog>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>ミルク</h5>
        </div>
        <milk-list :milks="home.milks" />
        <div class="px-5 py-2">
          <v-btn
            @click="isMilkDialog = true"
            color="primary"
            variant="outlined"
            block
            small>
            ミルク記録を追加
          </v-btn>
        </div>
        <v-dialog v-model="isMilkDialog" persistent>
          <milk-modal
            :isOpen="isMilkDialog"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport"
            @close="isMilkDialog = false" />
        </v-dialog>

        <div class="subtitle-wrapper pa-1 px-3">
          <h5>排便</h5>
        </div>
        <defecation-list :defecations="home.defecations" />
        <div class="px-5 py-2">
          <v-btn
            @click="isDefecationDialog = true"
            color="primary"
            variant="outlined"
            block
            small>
            排便記録を追加
          </v-btn>
        </div>
        <v-dialog v-model="isDefecationDialog" persistent>
          <defecation-modal
            :isOpen="isDefecationDialog"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport"
            @close="isDefecationDialog = false" />
        </v-dialog>

        <div v-if="!isChatEnabled && home.stateOfChild">
          <div class="subtitle-wrapper pa-1 px-3">
            <h5>お子様の記録</h5>
          </div>
          <state-of-child-form
            :isDraft="home.stateOfChild.isDraft"
            :body="home.stateOfChild.body"
            :childId="childId"
            :selectedDate="selectedDate"
            @posted="fetchReport" />
        </div>
      </div>
    </div>
  </div>
</template><script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import { when } from '@/service/Switch';

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

import EatingStatusItem from '@/components/molecules/EatingStatusItem.vue';
import SleepList from '@/components/organisms/RecordList/SleepList.vue';
import SleepModal from '@/components/modal/SleepModal.vue';
import TemperatureList from '@/components/organisms/RecordList/TemperatureList.vue';
import TemperatureModal from '@/components/modal/TemperatureModal.vue';
import DinnerList from '@/components/organisms/RecordList/DinnerList.vue';
import DinnerModal from '@/components/modal/DinnerModal.vue';
import BreakfastList from '@/components/organisms/RecordList/BreakfastList.vue';
import BreakfastModal from '@/components/modal/BreakfastModal.vue';
import MilkList from '@/components/organisms/RecordList/MilkList.vue';
import MilkModal from '@/components/modal/MilkModal.vue';
import DefecationList from '@/components/organisms/RecordList/DefecationList.vue';
import DefecationModal from '@/components/modal/DefecationModal.vue';
import StateOfChildForm from '@/components/organisms/RecordList/StateOfChildForm.vue';

import { meModule } from '@/store/entityModules/me';
import { dailyReportModule } from '@/store/entityModules/dailyReport';
import { attendanceModule } from '@/store/entityModules/attendance';
import { familyModule } from '@/store/entityModules/family';

// model
import { HomeAttendanceSchema } from '@API/src/component/home/attendance/types';
import {
  DefecationStatus,
  DefecationStatusType,
} from '../../node_modules/chaild-entity/src/component/home/defecation/types';
import { Defecation } from '../model/Others/Defecation';

interface DataType {
  childId: number;
  childName: string;
  selectedDate: string;
  isSleepDialog: boolean;
  isTemperatureDialog: boolean;
  isDinnerDialog: boolean;
  isBreakfastDialog: boolean;
  isMilkDialog: boolean;
  isDefecationDialog: boolean;
  fetchLoading: boolean;
  nurseryLoading: boolean;
  homeLoading: boolean;
}

export default defineComponent({
  components: {
    'vue-loading': Loading,
    EatingStatusItem,
    SleepList,
    SleepModal,
    TemperatureList,
    TemperatureModal,
    DinnerList,
    DinnerModal,
    BreakfastList,
    BreakfastModal,
    MilkList,
    MilkModal,
    DefecationList,
    DefecationModal,
    StateOfChildForm,
  },

  data: (): DataType => ({
    childId: NaN,
    childName: '',
    selectedDate: dayjs().format('YYYY-MM-DD'),
    isSleepDialog: false,
    isTemperatureDialog: false,
    isDinnerDialog: false,
    isBreakfastDialog: false,
    isMilkDialog: false,
    isDefecationDialog: false,
    fetchLoading: false,
    nurseryLoading: false,
    homeLoading: false,
  }),

  computed: {
    me() {
      return meModule.me;
    },
    home() {
      return dailyReportModule.home;
    },
    nursery() {
      return dailyReportModule.nursery;
    },
    meals() {
      return dailyReportModule.meals || [];
    },
    attendance(): HomeAttendanceSchema | null {
      return attendanceModule.attendance;
    },
    mealComment(): string {
      if (this.nursery.meal) {
        return this.nursery.meal.comment;
      }
      return '';
    },
    year(): string {
      return dayjs(this.selectedDate).format('YYYY');
    },
    today(): string {
      return dayjs(this.selectedDate).format('M月D日');
    },
    dayOfTheWeek(): string {
      return dayjs(this.selectedDate).format('ddd');
    },
    isChatEnabled() {
      if (familyModule.family) {
        const children = familyModule.family.children;
        const child = children.find((c) => {
          if (c.childId === this.childId) {
            return c;
          }
        });
        if (!!child && child.nurserySettings.chat.isEnabled) {
          return child.nurserySettings.chat.isEnabled;
        }
      }
      return false;
    },
  },

  watch: {
    // queryが変わるたびに実行される
    '$route.query': {
      handler(newQuery) {
        if (newQuery.childId) {
          this.fetchReport(); // ここでAPIを発行するメソッドを呼ぶ
        }
      },
      immediate: true // コンポーネントが最初に表示された時も実行する
    }
  },

  methods: {
    routerBack() {
      this.$router.replace('/record');
    },
    toNextDate() {
      this.selectedDate = dayjs(this.selectedDate)
        .add(1, 'day')
        .format('YYYY-MM-DD');
      this.$router.replace({
        query: {
          ...this.$route.query,
          chatDate: this.selectedDate
        }
      });
    },
    toPrevDate() {
      this.selectedDate = dayjs(this.selectedDate)
        .add(-1, 'day')
        .format('YYYY-MM-DD');
      this.$router.replace({
        query: {
          ...this.$route.query,
          chatDate: this.selectedDate
        }
      });
    },
    async fetchReport() {
      this.nurseryLoading = this.homeLoading = this.fetchLoading = true;

      const currentChildId = Number(this.$route.query.childId);

      const report = dailyReportModule.fetchReport({
        childId: currentChildId,
        date: this.selectedDate,
      });
      const attendance = attendanceModule.fetchAttendance({
        childId: currentChildId,
        date: this.selectedDate,
      });
      await Promise.all([report, attendance]);
      this.nurseryLoading = this.homeLoading = this.fetchLoading = false;
    },
    minuteFormat(iso: string | null) {
      if (iso !== null) {
        return dayjs(iso).format('HH:mm');
      }
      return null;
    },
    getDefecationState(state: DefecationStatusType): string {
      return when(state)
        .on(
          (v) => v === DefecationStatus.hard,
          () => '硬い',
        )
        .on(
          (v) => v === DefecationStatus.normal,
          () => '普通',
        )
        .on(
          (v) => v === DefecationStatus.soft,
          () => '軟らかい',
        )
        .on(
          (v) => v === DefecationStatus.diarrhea,
          () => '水様便（下痢）',
        )
        .otherwise(() => '');
    },
    toChat() {
      this.$router.push({
        name: 'Chat',
        query: {
          childId: String(this.childId),
          childName: this.childName,
          date: this.selectedDate,
        },
      });
    },
    categoryToText(milkCategory: string): string {
      return when(milkCategory)
        .on(
          (v) => v === 'formula',
          () => 'ミルク',
        )
        .on(
          (v) => v === 'breast',
          () => '母乳',
        )
        .otherwise(() => '');
    },
  },

  async mounted() {
    if (typeof this.$route.query.childName === 'string') {
      this.childName = this.$route.query.childName;
    }
    if (typeof this.$route.query.childId === 'string') {
      this.childId = Number(this.$route.query.childId);
    } else {
      this.routerBack();
    }

    if (typeof this.$route.query.chatDate === 'string') {
      this.selectedDate = String(this.$route.query.chatDate);
      this.fetchReport();
      // this.fetchAttendance();
    }
  },
});
</script>
<style lang="scss" scoped>
@use '../sass/variables.scss';

.wrapper {
  height: 100%; /* Fallback */
  overflow-y: scroll;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    -webkit-appearance: none;
  }
  @supports (bottom: env(safe-area-inset-bottom)) {
    padding-top: env(safe-area-inset-top);
  }

  .header-wrapper {
    height: 122px;
    width: 100%;
    position: fixed;
    z-index: 3;

    .date_card {
      background-color: #ffffff;
    }
    .outline {
      border: 1px solid rgb(var(--v-theme-primary));
    }
  }
  .grey-border {
    height: 2px;
    width: 100%;
    background-color: #f5f6f6;
  }

  .content-wrapper {
    height: calc(calc(var(--vh, 1vh) * 100) - 122px - 56px);
    margin: 130px 0 56px;
    overflow-y: scroll;
    // padding: 122px 0 48px;
    // height: 100%;
    // overflow-y: scroll;
    // -ms-overflow-style: none;
    // scrollbar-width: none;
    // &::-webkit-scrollbar {
    //   display: none;
    //   -webkit-appearance: none;
    // }
  }
}

.title-wrapper {
  color: #ffffff;
  background-color: rgb(var(--v-theme-primary));
}
.subtitle-wrapper {
  color: #a3a4a4;
  background-color: #f5f6f6;
}
.full {
  height: 30px;
  width: 30px;
  border-radius: 100%;
  background-color: rgb(var(--v-theme-primary));
}
</style>
